// frontend/src/pages/ProfilePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
    const { user, login } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState({
        _id: '',
        fullName: '',
        phoneNumber: '',
        email: '',
        isAdmin: false,
        walletBalance: 0,
        bankName: '',
        bankBranch: '',
        accountNumber: '',
        walletId: '',
        token: '',
        
    });

    // Removed: const [validationErrors, setValidationErrors] = useState({});

    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState('');
    const [editMode, setEditMode] = useState(false);

    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');

    const customAnimations = `
        @keyframes fade-in-down {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoom-in-out {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
        }
        @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        .animate-fade-in-down {
            animation: fade-in-down 0.8s ease-out forwards;
        }
        .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out forwards;
        }
        .animate-zoom-in-out {
            animation: zoom-in-out 0.5s ease-out forwards;
        }
        .animate-bounce-subtle {
            animation: bounce-subtle 1.5s infinite ease-in-out;
        }
        .animate-pulse-slow {
            animation: pulse-slow 3s infinite ease-in-out;
        }
    `;

    const fetchUserProfile = useCallback(async () => {
        setLoading(true);
        setError('');
        if (!user || !user.token) {
            setError('You must be logged in to view your profile.');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/users/profile`, {
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch user profile.');
            }

            const data = await response.json();
            setProfile(data);
        } catch (err) {
            setError(err.message || 'Error fetching profile.');
            toast.error(err.message || 'Error fetching profile.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user && user.token) {
            fetchUserProfile();
        } else {
            setLoading(false);
            setError('You must be logged in to view your profile.');
        }
    }, [user, fetchUserProfile]);

    // Removed: validateField function
    // Removed: validateForm function

    // Modified: handleFieldChange - no validation logic
    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));
        // Removed: real-time validation call
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setUpdating(true);
        setError('');

        if (!user || !user.token) {
            toast.error('You must be logged in to update your profile.');
            setUpdating(false);
            return;
        }

        // Removed: if (!validateForm()) { ... } check

        const updateData = {
            fullName: profile.fullName,
            phoneNumber: profile.phoneNumber,
            email: profile.email,
            bankName: profile.bankName,
            bankBranch: profile.bankBranch,
            accountNumber: profile.accountNumber,
            walletId: profile.walletId,
        };

        // Simplified password update logic
        if (newPassword && newPassword === confirmNewPassword && currentPassword) {
            updateData.password = newPassword;
            updateData.currentPassword = currentPassword;
        } else if (newPassword || confirmNewPassword || currentPassword) {
            // If any password field is touched but not all conditions met for a valid change
            // Frontend won't warn, but backend might still have issues if any implicit checks exist.
            // With backend validation removed, this will likely just pass through.
            if (newPassword !== confirmNewPassword) {
                toast.error('New password and confirm password do not match.');
                setUpdating(false);
                return;
            }
            if (newPassword && newPassword.length < 6) {
                toast.error('New password must be at least 6 characters long.');
                setUpdating(false);
                return;
            }
            if (newPassword && !currentPassword) {
                toast.error('Please enter your current password to change.');
                setUpdating(false);
                return;
            }
        }


        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/users/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify(updateData),
            });

            const data = await response.json();

            if (!response.ok) {
                 // Even with frontend validation removed, the backend might still send error messages
                 // for things like "Invalid current password" if that specific check remains in userController.js
                throw new Error(data.message || 'Failed to update profile.');
            }

            toast.success(data.message || 'Profile updated successfully!');
            setProfile(data);
            if (data.token) {
                login(data);
            } else {
                login({ ...user, ...data });
            }
            setEditMode(false);
            setNewPassword('');
            setConfirmNewPassword('');
            setCurrentPassword('');
            // Removed: setValidationErrors({});

        } catch (err) {
            setError(err.message || 'Error updating profile.');
            toast.error(err.message || 'Error updating profile.');
        } finally {
            setUpdating(false);
        }
    };

    const handleDepositClick = () => {
        navigate('/deposit');
    };

    const handleWithdrawalClick = () => {
        toast.info('Provided your eZ Cash number? You’re all set! If not, share it now to grab your winnings!');
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-200 font-inter">
                <style>{customAnimations}</style>
                <div className="text-center">
                    <p className="text-2xl font-semibold text-purple-700 animate-pulse-slow">
                        Loading your profile...
                    </p>
                    <div className="mt-4 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100 font-inter">
                <style>{customAnimations}</style>
                <div className="bg-white p-8 rounded-lg shadow-xl border-t-8 border-red-500 w-full max-w-md text-center animate-zoom-in-out">
                    <h2 className="text-3xl font-bold text-red-700 mb-4">Error!</h2>
                    <p className="text-lg text-gray-700">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-6 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300 shadow-md"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 md:p-8 mt-8 font-inter bg-gradient-to-br from-green-50 to-blue-100 min-h-screen">
            <style>{customAnimations}</style>

            <h1 className="text-4xl font-extrabold text-center mb-10 text-green-800 animate-fade-in-down">
                👤 My Profile 🚀
            </h1>

            <div className="bg-white p-6 rounded-2xl shadow-3xl border-t-8 border-green-500 max-w-2xl mx-auto animate-zoom-in-out">
                <h2 className="text-3xl font-bold text-center mb-6 text-gray-800">
                    {editMode ? 'Edit Your Details' : 'Your Personal Information'}
                </h2>

                <div className="text-center bg-blue-50 p-4 rounded-lg shadow-inner mb-6 animate-bounce-subtle">
                    <p className="text-xl font-bold text-blue-700">Wallet Balance:</p>
                    <p className="text-5xl font-extrabold text-green-600 leading-none mt-2">
                        Rs. {profile.walletBalance ? profile.walletBalance.toFixed(2) : '0.00'}
                    </p>
                    <div className="flex justify-center gap-4 mt-4">
                        <button
                            onClick={handleDepositClick}
                            className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-bold py-2 px-4 rounded-full shadow-lg transition duration-300 transform hover:scale-105 text-sm"
                        >
                            Deposit Funds
                        </button>
                        <button
                            onClick={handleWithdrawalClick}
                            className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-2 px-4 rounded-full shadow-lg transition duration-300 transform hover:scale-105 text-sm"
                        >
                            Withdraw Funds
                        </button>
                    </div>
                </div>

                {!editMode ? (
                    <div className="space-y-4 text-lg text-gray-700">
                        <p><span className="font-semibold text-gray-800">Full Name:</span> {profile.fullName}</p>
                        <p><span className="font-semibold text-gray-800">Email:</span> {profile.email}</p>
                        <p><span className="font-semibold text-gray-800">Phone Number:</span> {profile.phoneNumber}</p>
                        <p><span className="font-semibold text-gray-800">Bank Name:</span> {profile.bankName || 'Not set'}</p>
                        <p><span className="font-semibold text-gray-800">Bank Branch:</span> {profile.bankBranch || 'Not set'}</p>
                        <p><span className="font-semibold text-gray-800">Account Number:</span> {profile.accountNumber || 'Not set'}</p>
                        <p>
                        <span className="font-semibold text-gray-800">Wallet ID (e.g., eZ Cash):</span> {profile.walletId || 'Not set'} 
                        <span className="text-red-600"> — Winnings will only be transferred to this eZ Cash account.</span>
                        </p>

                        

                        <div className="mt-6 text-center">
                            <button
                                onClick={() => setEditMode(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md shadow-lg transition duration-300 transform hover:scale-105"
                            >
                                Edit Profile
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleUpdateProfile} className="space-y-4">
                        <div>
                            <label htmlFor="fullName" className="block text-gray-700 text-md font-bold mb-2">Full Name</label>
                            <input
                                type="text"
                                id="fullName"
                                name="fullName"
                                value={profile.fullName}
                                onChange={handleFieldChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                            {/* Removed: Error message display */}
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-gray-700 text-md font-bold mb-2">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={profile.email}
                                onChange={handleFieldChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                            {/* Removed: Error message display */}
                        </div>
                        <div>
                            <label htmlFor="phoneNumber" className="block text-gray-700 text-md font-bold mb-2">Phone Number</label>
                            <input
                                type="text"
                                id="phoneNumber"
                                name="phoneNumber"
                                value={profile.phoneNumber}
                                onChange={handleFieldChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                            {/* Removed: Error message display */}
                        </div>
                        <div>
                            <label htmlFor="bankName" className="block text-gray-700 text-md font-bold mb-2">Bank Name</label>
                            <input
                                type="text"
                                id="bankName"
                                name="bankName"
                                value={profile.bankName || ''}
                                onChange={handleFieldChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., Commercial Bank"
                                required
                            />
                            {/* Removed: Error message display */}
                        </div>
                        <div>
                            <label htmlFor="bankBranch" className="block text-gray-700 text-md font-bold mb-2">Bank Branch</label>
                            <input
                                type="text"
                                id="bankBranch"
                                name="bankBranch"
                                value={profile.bankBranch || ''}
                                onChange={handleFieldChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., Galle Branch"
                                required
                            />
                            {/* Removed: Error message display */}
                        </div>
                        <div>
                            <label htmlFor="accountNumber" className="block text-gray-700 text-md font-bold mb-2">Bank Account Number</label>
                            <input
                                type="text"
                                id="accountNumber"
                                name="accountNumber"
                                value={profile.accountNumber || ''}
                                onChange={handleFieldChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Your bank account number"
                                required
                            />
                            {/* Removed: Error message display */}
                        </div>
                        <div>
                            <label htmlFor="walletId" className="block text-gray-700 text-md font-bold mb-2">Mobile Wallet ID (e.g., eZ Cash )</label>
                            <input
                                type="text"
                                id="walletId"
                                name="walletId"
                                value={profile.walletId || ''}
                                onChange={handleFieldChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Your mobile wallet ID/number"
                                required
                            />
                        </div>

                        {/* Password Change Section */}
                        <div className="pt-4 border-t border-gray-200 mt-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Change Password (Optional)</h3>
                            <p className="text-sm text-gray-600 mb-4">Leave blank if you don't want to change your password.</p>
                            <div>
                                <label htmlFor="currentPassword" className="block text-gray-700 text-md font-bold mb-2">Current Password</label>
                                <input
                                    type="password"
                                    id="currentPassword"
                                    name="currentPassword"
                                    value={currentPassword}
                                    onChange={(e) => {
                                        setCurrentPassword(e.target.value);
                                        // Removed: setValidationErrors call
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter your current password"
                                    autoComplete="current-password"
                                />
                                {/* Removed: Error message display */}
                            </div>
                            <div className="mt-4">
                                <label htmlFor="newPassword" className="block text-gray-700 text-md font-bold mb-2">New Password</label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        // Removed: validation logic and setValidationErrors
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter new password (min 6 chars)"
                                    autoComplete="new-password"
                                />
                                {/* Removed: Error message display */}
                            </div>
                            <div className="mt-4">
                                <label htmlFor="confirmNewPassword" className="block text-gray-700 text-md font-bold mb-2">Confirm New Password</label>
                                <input
                                    type="password"
                                    id="confirmNewPassword"
                                    name="confirmNewPassword"
                                    value={confirmNewPassword}
                                    onChange={(e) => {
                                        setConfirmNewPassword(e.target.value);
                                        // Removed: validation logic and setValidationErrors
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Confirm new password"
                                    autoComplete="new-password"
                                />
                                {/* Removed: Error message display */}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <button
                                type="button"
                                onClick={() => {
                                    setEditMode(false);
                                    fetchUserProfile();
                                    setNewPassword('');
                                    setConfirmNewPassword('');
                                    setCurrentPassword('');
                                    // Removed: setValidationErrors({});
                                }}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-md shadow-md transition duration-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-md shadow-lg transition duration-300 transform hover:scale-105"
                                disabled={updating}
                            >
                                {updating ? 'Updating...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ProfilePage;